import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import * as sessionUserSelector from 'selectors/sessionUser'
import ticketFilterVisibility from 'constants/ticketFilterVisibility'

import Button from 'components/Shared/Button/Index'
import ModalFormContainer from 'components/Shared/ModalForm/Index'
import ConditionsSection from './ConditionsSection'

const getVisibility = (editingFilter) => {
  let visibility
  if (!editingFilter || !editingFilter.global) {
    visibility = ticketFilterVisibility.PRIVATE
  } else {
    visibility = editingFilter.published ? ticketFilterVisibility.ALL : ticketFilterVisibility.ADMINS
  }
  return visibility
}

const AddEditFilterForm = ({
  visible, onSubmit, onCloseForm, editingFilter, onDeleteFilter,
  ticketingSystemDisplayId, partnerStatuses, isNewLogic,
  providerTicketBoards, selectedProviderTicketBoardId,
}) => {
  const { t } = useTranslation()
  const isAdmin = useSelector(sessionUserSelector.isAdmin)

  const onSubmit_ = (values) => {
    onSubmit(values)
  }

  const onDelete = () => {
    onDeleteFilter?.(editingFilter)
  }

  const generalFormFieldParams = [
    {
      name: 'name',
      label: t('ticketsPage.filterForm.fieldLabelName'),
      type: 'text',
      required: true,
    },
  ]

  if (isAdmin && !isNewLogic) {
    const isGlobal = Boolean(editingFilter?.global)
    const visibilityFieldParams = {
      name: 'visibility',
      label: t('ticketsPage.filterForm.fieldLabelVisibility'),
      type: 'toggleButtonGroup',
      buttons: [
        {
          value: 'private',
          label: t('ticketsPage.filterForm.visibilityPrivate'),
          disabled: !!editingFilter,
        },
        {
          value: 'admins',
          label: t('ticketsPage.filterForm.visibilityAdmins'),
          disabled: editingFilter && !isGlobal,
        },
        {
          value: 'all',
          label: t('ticketsPage.filterForm.visibilityAllUsers'),
          disabled: editingFilter && !isGlobal,
        },
      ],
    }
    generalFormFieldParams.push(visibilityFieldParams)
  }

  if (providerTicketBoards?.length > 1) {
    const providerTicketBoardsFieldParams = {
      name: 'providerTicketBoards',
      label: t('ticketsPage.filterForm.providerTicketBoards'),
      type: 'select',
      options: providerTicketBoards.map(board => ({
        value: board.id,
        label: board.name,
      })),
      isMultiselect: true,
      required: false,
      placeholder: t('ticketsPage.filterForm.providerTicketBoardsPlaceholder'),
    }

    generalFormFieldParams.push(providerTicketBoardsFieldParams)
  }

  const fieldParams = [
    {
      group: t('ticketsPage.filterForm.generalInformation'),
      fieldParams: generalFormFieldParams,
    },
  ]

  const isEdit = !!editingFilter

  const additionalButton = isEdit && (
    <Button
      large
      alert
      onClick={onDelete}
    >
      {t('ticketsPage.filterForm.deleteFilter')}
    </Button>
  )

  const formValidator = (values) => {
    const { query, advanced, conditions } = values
    const errors = {}
    if (advanced && !query.trim()) {
      errors.noQuery = true
    }

    if (!advanced) {
      const hasEmptyConditions = conditions.some(c => {
        const value = c.value.trim()
        return Boolean(
          !c.column ||
          !c.equality ||
          (!value && c.equality !== 'empty' && c.equality !== 'not_empty')
        )
      })
      if (hasEmptyConditions) {
        errors.emptyCondition = true
      }
    }
    return errors
  }

  const visibility = getVisibility(editingFilter)
  const initialValues = {
    name: editingFilter?.name || '',
    visibility: visibility,
    query: (editingFilter?.advanced && editingFilter?.query) || '',
    conditions: editingFilter?.conditions || [{
      column: '',
      equality: '',
      value: '',
    }],
    advanced: Boolean(editingFilter?.advanced),
    providerTicketBoards: editingFilter?.providerTicketBoardIds || [selectedProviderTicketBoardId],
  }

  return (
    <ModalFormContainer
      onClose={onCloseForm}
      visible={visible}
      actionButtonTitle={t('ticketsPage.filterForm.saveFilter')}
      additionalButton={additionalButton}
      onSubmit={onSubmit_}
      title={t('ticketsPage.filterForm.title')}
      fieldParams={fieldParams}
      initialValues={initialValues}
      formValidator={formValidator}
      flatFieldNames
    >
      <ConditionsSection
        ticketingSystemDisplayId={ticketingSystemDisplayId}
        partnerStatuses={partnerStatuses}
      />
    </ModalFormContainer>
  )
}

AddEditFilterForm.propTypes = {
  visible: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCloseForm: PropTypes.func.isRequired,
  editingFilter: PropTypes.object,
  onDeleteFilter: PropTypes.func,
  ticketingSystemDisplayId: PropTypes.string.isRequired,
  partnerStatuses: PropTypes.array.isRequired,
  isNewLogic: PropTypes.bool,
  providerTicketBoards: PropTypes.array,
  selectedProviderTicketBoardId: PropTypes.string,
}

export default AddEditFilterForm
